<template>
    <article>
        <div class="article-content">
            <h1>How to use the {{ selectedYear }} fee survey data room</h1>
            <p>LCP’s fee survey data room lets you easily view investment manager fees by asset classes. It uses data from the LCP Investment Fee Survey {{ selectedYear }}. We have grouped mandates under the following asset groups and you can navigate to the charts using the menu on the left. Fees are shown in AMC and OCF (these are defined at the bottom of this page). Thank you to the investment managers who responded to our survey, see the <router-link :to="{ name: 'respondents', params: { year: selectedYear } }"><span>list of respondents</span></router-link>. </p>
            <p>If you would like to discuss these findings in more detail or benchmark your own pension scheme, please get in touch with <router-link to="/contact">one of our experts</router-link></p>
            <div v-if="mattContact">
                <img :src="mattContact.image" :alt="mattContact.name" class="matt-gibson" />
                <p><strong>
                    <a v-if="mattContact.url" :href="mattContact.url" target="_blank">{{ mattContact.name }}</a>
                    <template v-else>{{ mattContact.name }}</template>
                </strong><br/>
                {{ mattContact.role }}</p>
            </div>
            <h4>Fee definitions</h4>
            <ol type="a">
                <li>Annual Management Charge (AMC) the headline quoted annual fee rate applied to the value of assets under management. The AMC is often quoted in the form of basis points per annum (bps pa). A fee of 50 bps pa (or 0.5% pa) means that each year the manager would earn £5,000 for every £1million of assets managed.</li>
                <li>Ongoing charges figure (OCF) consists principally of the investment manager annual fee and the cost for other services paid for by the pooled fund, such as the fees paid to the custodian, auditors, administrators, managing agents and trustees of the pooled fund. It excludes entry and exit costs and performance related fees, if applicable</li>
            </ol>
        </div>
    </article>
</template>

<script setup lang="ts">
    import { ref, onMounted, watch } from 'vue';
    import axios from 'axios';
    import { useRoute } from 'vue-router';
    import { useYear } from '../composables/useYear';
    import { useHead } from '@unhead/vue';
    import { BASE_URL } from '../config';


    interface Contact {
        name: string;
        role: string;
        email: string;
        telephone: string;
        image: string;
        url: string | null;
    }

    const { selectedYear } = useYear();
    const route = useRoute();

    // Sync year from route
    watch(() => route.params.year, (newYear) => {
        if (newYear && typeof newYear === 'string' && newYear !== selectedYear.value) {
            selectedYear.value = newYear;
        }
    }, { immediate: true });

    const mattContact = ref<Contact | null>(null);

    useHead({
        title: 'How to use the fee survey data room',
        meta: [
            { name: 'description', content: 'Guide on how to use LCP’s fee survey data room.' },
            { property: 'og:title', content: 'How to use the fee survey data room - LCP Investment Fee Survey' },
            { property: 'og:description', content: 'Guide on how to use LCP’s fee survey data room.' },
            { property: 'og:url', content: `${BASE_URL}/contents` },
            { property: 'og:image', content: `${BASE_URL}/images/ifs-social.png` },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: 'How to use the fee survey data room - LCP Investment Fee Survey' },
            { name: 'twitter:description', content: 'Guide on how to use LCP’s fee survey data room.' },
            { name: 'twitter:image', content: `${BASE_URL}/images/ifs-social.png` }
        ]
    })

    onMounted(async () => {
        try {
            const response = await axios.get<Contact[]>('/data/contacts.json');
            mattContact.value = response.data.find((c) => c.name === 'Matt Gibson') || null;
        } catch (error) {
            console.error('Failed to load contacts:', error);
        }
    });
</script>

<style lang="scss" scoped>
    .feeWrapper {
        margin: 20px 0 0 0;
    }
    .feesImage {
        margin: 0 10px 10px 0;
        display: block;
    }
    .how-to-use-menu__img {
        width: 60px;
        height: 60px;
        float: left;
        margin: 2px 10px 10px 0;
    }
    strong {
        font-weight: 700;
    }
    .matt-gibson {
        width: 200px;
        margin-top: 10px;
        border-radius: 20px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    }
    .fade-enter-active, .fade-leave-active {
        transition: opacity .5s
    }
    .fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
        opacity: 0
    }
    .article-content {
        position: relative;
        padding: 25px;
    }
    h2, h3, h4 {
        font-weight: 700;
    }
    h4 {
        margin: 20px 0 0 0;
    }
    h1 {
        font-size: 1.5rem;
        font-weight: 700;
        padding: 0 0 20px 0;
        color: var(--lcp-blue);
        position: relative;
        margin-bottom: 20px;
        display: inline-block;

        // Horizontal underline with fade - responsive to text width
        &::before {
            content: '';
            position: absolute;
            bottom: 8px;
            left: 0;
            width: calc(100% - 10px);
            height: 3px;
            background: linear-gradient(to right,
                var(--investment-violet) 0%,
                var(--investment-violet) 50%,
                transparent 100%);
            border-radius: 2px;
        }

        // Small accent dot at start
        &::after {
            content: '';
            position: absolute;
            bottom: 5px;
            left: 0px;
            width: 9px;
            height: 9px;
            background: var(--investment-violet);
            border-radius: 50%;
        }
    }
    h3 {
        font-size: 1.1rem;
        line-height: 1.8rem;
    }
    ol {
        margin: 0px 0 0 10px;
        li {
            font-size: 0.9rem;
            margin: 0 0 10px 10px;
            list-style-type: lower-alpha;
        }
        li:last-child {
            margin: 0 0 0 10px;
        }
    }
    li span {
        font-style: italic;
    }
    li {
        margin: 0 0 10px 0;
    }
    ul {
        margin: 20px 0 10px 0;
        li:last-child {
            padding-bottom: 35px;
            margin: 0;
        }
    }
    a, a:active, a:visited {
        color: var(--lcp-blue);
        font-weight: 700;
    }
    a:hover {
        color: var(--dark-orange);
    }
    @media only screen  and (max-width : 800px) and (orientation : portrait) {
        .left-pane, .right-pane {
            padding: 0;
        }
        .article-content {
            display: block;
            margin: 0;
            padding: 25px;
            overflow: hidden;
        }
        article {
            min-width: 100%;
            display: block;
            overflow: auto;
        }
    }



    @media only screen and (max-width : 820px) and (orientation : portrait),
    screen and (max-width : 1366px) and (orientation : landscape) {
        .desc {
            width: calc(100% - 50px);
        }
    }
    @media only screen and (max-width : 480px) and (orientation : portrait),
    screen and (max-width : 768px) and (orientation : landscape)
    {
        .desc {
            width: calc(100% - 30px);
        }
        .article-content {
            padding: 15px;
        }
    }
    .how-to-use-menu__container {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 10px;
    }
    .how-to-link {
        display: block;
    }
    .how-to-use-menu__items {
        flex: 1 1 100%;
    }
</style>
